import React, { useState } from "react";
import ChatAgent from "./ChatAgent";
import DownloadReportDialog from "./DownloadReportDialog";
import { useFocusAgent } from "./TheSeaLayout";
import SEO from "../SEO";
import config from "../../config";

const AGENCIES = [
  { abbr: "ICH", name: "International Council for Harmonisation", desc: "Harmonized quality guidelines spanning impurities, stability, and validation." },
  { abbr: "EMA", name: "European Medicines Agency", desc: "Scientific guidelines, Q&A documents, and referral outcomes affecting quality dossiers." },
  { abbr: "FDA", name: "U.S. Food & Drug Administration", desc: "Draft and final guidance, warning letters, and inspection observation trends." },
  { abbr: "PMDA", name: "Japan Pharmaceuticals & Medical Devices Agency", desc: "Japan-specific manufacturing and quality notices, including local deviations from ICH." },
  { abbr: "MHRA", name: "UK Medicines & Healthcare products Regulatory Agency", desc: "Post-Brexit divergence tracking for UK-specific CMC expectations." },
  { abbr: "WHO", name: "World Health Organization", desc: "Prequalification standards and manufacturing requirements for global health products." },
];

const TAGS = [
  "Elemental Impurities", "Nitrosamines", "Stability & Shelf Life", "Analytical Method Validation",
  "Process Validation", "Manufacturing Changes & Comparability", "Container Closure Systems",
  "Data Integrity", "Post-Approval Changes", "Continuous Manufacturing", "Biologics CMC", "Generic & ANDA CMC",
  "and many more...",
];

const QUOTES = [
  { quote: "I typed one question and had a sourced answer before I'd have finished searching the agency website myself.", cite: "Senior Scientist, mid-size biotech" },
  { quote: "Comparing to a leading general AI agent, askTheSea output goes further by turning the same topic into a regulatory filing strategy, including references, synthetic examples, risk areas, mitigation strategies, and an approved-product precedent.", cite: "Regulatory Affairs Consultant" },
];

const PAIN_POINTS = [
  { title: "Fragmented sources", desc: "Relevant guidance lives across dozens of agency portals, PDF libraries, and meeting minutes. Someone still has to go find it." },
  { title: "Signal buried in noise", desc: "Most regulatory updates aren't CMC-relevant. The few that are get lost between clinical, safety, and commercial notices." },
  { title: "No time to spare", desc: "Filing deadlines and health authority questions don't wait for a quarterly literature review to catch up." },
];

const WAYPOINTS = [
  { num: "01 — SCAN", title: "Continuous monitoring", desc: "askTheSea watches publicly available regulatory sources across major health authorities and standards bodies, around the clock." },
  { num: "02 — DISTILL", title: "CMC relevance filter", desc: "Each item is screened and tagged for CMC relevance — impurities, stability, manufacturing changes, analytical methods, and more." },
  { num: "03 — ANSWER", title: "Ask, get a sourced answer", desc: "Ask a question in plain English and the agent responds directly — plus the specific public source behind it. No report to wait for, no PDF to dig through." },
];

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function TheSeaHome() {
  const focusAgent = useFocusAgent();
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  return (
    <>
    <SEO
      title="AskTheSea — CMC Regulatory Intelligence"
      description="AskTheSea consolidates regulatory guidelines, white papers, and public databases so CMC teams can ask a question and get a sourced answer."
      url={`${config.BaseUrl}/`}
      image={`${config.BaseUrl}/assets/logo.png?v=2`}
    />
    <section className="page active" id="page-home">
      <div className="wrap hero">
        <div>
          <div className="eyebrow">CMC* Regulatory Intelligence</div>
          <h1>askTheSea (ask The Sea) - AI powered CMC Regulatory Intelligence through Search</h1>
          <p className="lede">askTheSea consolidate regulatory guidelines, laws, Consortia white papers, and public databases for you, so you can focus on building regulatory CMC strategies with reduced risks and accelerated timelines.</p>
          <p className="lede">Regulatory intelligence designed for Biotechnology and Pharmaceutical CMC Scientists.</p>
          <p className="footnote">* CMC stands for Chemistry, Manufacturing, and Controls - a critical component of drug development ensuring a drug is consistently safe, effective, and high-quality.</p>
          <div className="hero-ctas">
            <button className="btn btn-primary" onClick={focusAgent}>askTheSea</button>
            <button className="btn btn-ghost" onClick={() => scrollToSection("how-it-works")}>See how it works</button>
          </div>
        </div>
        <ChatAgent />
      </div>
      <DownloadReportDialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} />

      <div className="wrap">
        <section className="tight">
          <div className="section-head">
            <div className="eyebrow">Waters we chart</div>
            <h2>Six sources. One place to ask.</h2>
            <p>Every source below is indexed and searchable by askTheSea's agent — ask a question and it knows where to look, drawing only from what they publish openly. Hallucination-free.</p>
          </div>
          <div className="agency-grid">
            {AGENCIES.map((a) => (
              <div className="agency-card" key={a.abbr}>
                <div className="abbr">{a.abbr}</div>
                <div className="full-name">{a.name}</div>
                <p>{a.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="tight">
          <div className="section-head">
            <div className="eyebrow">Coverage</div>
            <h2>Domains we track inside CMC.</h2>
          </div>
          <div className="tag-cloud">
            {TAGS.map((t) => <span className="tag-pill" key={t}>{t}</span>)}
          </div>
        </section>

        <section className="tight">
          <div className="section-head">
            <div className="eyebrow">The problem</div>
            <h2>Watching the water alone doesn't scale.</h2>
          </div>
          <div className="three-col">
            {PAIN_POINTS.map((p) => (
              <div className="pain-card" key={p.title}>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="tight" id="how-it-works">
          <div className="section-head">
            <div className="eyebrow">How it works</div>
            <h2>Scan. Distill. Deliver.</h2>
          </div>
          <div className="waypoints">
            {WAYPOINTS.map((w) => (
              <div className="waypoint" key={w.num}>
                <span className="num">{w.num}</span>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "2em" }}>
            <button className="btn btn-primary" onClick={() => setReportDialogOpen(true)}>
              <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                <path d="M6 2.5h5.5L15 6v10a1 1 0 01-1 1H6a1 1 0 01-1-1V3.5a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                <path d="M11.5 2.5V6H15" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                <path d="M6.8 9.5h6.4M6.8 12h6.4M6.8 14.5h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              Download a FREE sample report
            </button>
          </div>
        </section>

        <section className="tight">
          <div className="quote-stack">
            {QUOTES.map((q) => (
              <div className="quote-block" key={q.cite}>
                <div className="qmark">&ldquo;</div>
                <div>
                  <blockquote>{q.quote}<span className="qmark qmark-close">&rdquo;</span></blockquote>
                  <cite>{q.cite}</cite>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="cta-banner">
            <div>
              <h2>Ready to stop searching six agency websites yourself?</h2>
              <p>askTheSea a question and get an answer with the source quoted — in seconds, not a quarterly review.</p>
            </div>
            <div className="cta-side">
              <button className="btn btn-on-dark" onClick={focusAgent}>askTheSea</button>
            </div>
          </div>
        </section>
      </div>
    </section>
    </>
  );
}
